// Portfolio Administration v1.0 PR 6 — list view of the waterfall codes
// with current status, active version info, and trailing-30-day run
// activity. Clicking a card navigates to the /portfolio-admin/waterfalls/:code
// detail page.
//
// Payment System v3.0 PR 3c (design spec §6.4): the canonical model is the
// unified WF1+WF2. WF3-WF6 are retained only as a display fallback so a
// historical WaterfallRun tagged with a legacy code still resolves a human
// name; the summary API no longer emits them.

import { Link } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { ChevronRight } from "lucide-react";
import type { WaterfallCodeSummaryDto } from "@/api/portfolioAdmin/pr6Types";
import { WaterfallStatusBadge } from "./WaterfallStatusBadge";
import { isoDateInTz, useDateFormatter } from "@/utils/datetime";

// Canonical unified model.
const WATERFALL_NAMES: Record<string, string> = {
  WF1: "Retained-share waterfall",
  WF2: "Contributed-share waterfall",
};

// Legacy WF3-WF6 names — display fallback for historical runs only.
const LEGACY_WATERFALL_NAMES: Record<string, string> = {
  WF3: "SPV Vendor Income",
  WF4: "PMVFI Recovery",
  WF5: "Closing Proceeds",
  WF6: "Prepayment Event",
};

function waterfallName(code: string): string {
  return WATERFALL_NAMES[code] ?? LEGACY_WATERFALL_NAMES[code] ?? code;
}

function fmtCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtDate(iso: string | null | undefined, tz: string): string {
  return isoDateInTz(iso, tz) || "—";
}

function effectiveStatus(row: WaterfallCodeSummaryDto): string {
  if (row.pending?.status === "proposed") return "proposed";
  if (row.active) return "active";
  if (row.pending?.status === "draft") return "draft";
  return "draft";
}

interface WaterfallsListProps {
  summaries: WaterfallCodeSummaryDto[];
}

export function WaterfallsList({ summaries }: WaterfallsListProps) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-space-3"
      data-testid="waterfalls-list"
    >
      {summaries.map((row) => (
        <WaterfallSummaryCard key={row.waterfallCode} row={row} />
      ))}
    </div>
  );
}

function WaterfallSummaryCard({ row }: { row: WaterfallCodeSummaryDto }) {
  const status = effectiveStatus(row);
  const name = waterfallName(row.waterfallCode);
  const { tz } = useDateFormatter();
  return (
    <Link
      to={`/portfolio-admin/waterfalls/${row.waterfallCode}`}
      className="block focus-visible:outline-none focus-visible:shadow-goldRing rounded-md"
      data-testid={`waterfalls-card-${row.waterfallCode}`}
    >
      <Card className="flex flex-col gap-space-2 hover:border-gold transition-colors">
        <div className="flex items-start justify-between gap-space-2">
          <div className="flex flex-col">
            <div className="flex items-center gap-space-2">
              <h2 className="font-display font-bold text-h3 m-0">
                {row.waterfallCode}
              </h2>
              <span className="text-small text-gray-mid">— {name}</span>
            </div>
            {row.authority && (
              <p className="text-caption text-gray-mid font-mono m-0">
                {row.authority}
              </p>
            )}
          </div>
          <div className="flex items-center gap-space-2">
            <WaterfallStatusBadge status={status} />
            <ChevronRight className="text-gray-mid" size={16} aria-hidden="true" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-2 text-small">
          <div>
            <p className="m-0 text-caption text-gray-mid uppercase tracking-[0.16em]">
              Active version
            </p>
            {row.active ? (
              <p className="m-0">
                v{row.active.version} · effective {fmtDate(row.active.effectiveStartDate, tz)}
              </p>
            ) : (
              <p className="m-0 text-gray-mid italic">No active version yet</p>
            )}
          </div>
          <div>
            <p className="m-0 text-caption text-gray-mid uppercase tracking-[0.16em]">
              Pending version
            </p>
            {row.pending ? (
              <p className="m-0">
                v{row.pending.version} · <em>{row.pending.status}</em>
              </p>
            ) : (
              <p className="m-0 text-gray-mid italic">None</p>
            )}
          </div>
        </div>

        <div className="text-caption text-gray-mid border-t border-border-light pt-space-2">
          Last 30 days:{" "}
          <strong>{row.recentRunCount}</strong>{" "}
          {row.recentRunCount === 1 ? "run" : "runs"} ·{" "}
          {fmtCurrency(row.recentTotalDistributed)} distributed
        </div>
      </Card>
    </Link>
  );
}
